import { Helmet } from 'react-helmet-async'

// ─── SEO ─────────────────────────────────────────────────────────────────────
//
// Per-page SEO metadata wrapper. Sets:
//   - <title>
//   - <meta name="description">
//   - <meta property="og:*"> for nicer social link previews
//   - <link rel="canonical"> when a path is given
//   - Optional JSON-LD structured data (e.g. FAQPage schema)
//
// Used on every public marketing page. Keep titles under ~60 chars and
// descriptions under ~155 chars so Google doesn't truncate them in the
// search results.

const SITE_NAME = 'Receipt Chaser'
const SITE_URL  = 'https://receiptchaser.com'

export default function SEO({
  title,
  description,
  path,            // e.g. "/features" — used for canonical + og:url
  noindex,         // pass true for thin/duplicate pages
  jsonLd,          // optional structured-data object
}) {
  const fullTitle = title.includes(SITE_NAME)
    ? title
    : `${title} | ${SITE_NAME}`

  const canonical = path ? `${SITE_URL}${path}` : SITE_URL

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}

      {/* Canonical helps Google understand which version of a URL is primary */}
      <link rel="canonical" href={canonical} />

      {/* Open Graph (LinkedIn, WhatsApp, Slack previews) */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content={SITE_NAME} />

      {/* Twitter card */}
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}

      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  )
}
